<div class="project-page">
  <div class="project-shell">
    <div class="page-header">
      <div>
        <a class="back-button" routerLink="/projects">‹ Projects</a>
        <h1 class="eyebrow">
          {{ editing() ? 'Project workspace' : 'New project' }}
        </h1>
        <p class="insights-lead subtitle">
          @if (editing()) { Refine the details and manage who can collaborate on
          this project. } @else { Set up the essentials now, then invite
          collaborators once the project is created. }
        </p>
      </div>

      @if (editing()) {
      <span class="metric-note">
        {{ projectUsersCount() }} {{ projectUsersCount() === 1 ? 'member' :
        'members' }}
      </span>
      }
    </div>

    <div class="project-content-grid">
      <section class="editor-card">
        <div class="card-header">
          <div>
            <h2 class="eyebrow">Project Details</h2>
            <p>Update the project profile, description, and brand surface.</p>
          </div>
          <div class="fields-header-actions">
            @if (editing() && project().id && areCoreDirty()) {
            <span class="field-unsaved-pill">Unsaved changes</span>
            }
            <button
              type="submit"
              form="core-project-form"
              class="btn btn-primary submit-button card-header-action"
              [disabled]="isPersisting()"
            >
              @if (isSavingCore()) {
              <span
                class="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              ></span>
              } {{ editing() ? 'Update Project' : 'Create Project' }}
            </button>
          </div>
        </div>

        <form
          id="core-project-form"
          (ngSubmit)="saveProject()"
          #form="ngForm"
          autocomplete="off"
        >
          <div class="form-group">
            <label for="name">Project name</label>
            <div class="input-with-icon">
              <i class="bi bi-building"></i>
              <input
                type="text"
                class="form-control"
                id="name"
                name="name"
                [ngModel]="project().name"
                (ngModelChange)="updateProjectName($event)"
                placeholder="Project name"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label for="description">Project description</label>
            <div class="input-with-icon">
              <i class="bi bi-file-earmark-text"></i>
              <input
                type="text"
                class="form-control"
                id="description"
                name="description"
                [ngModel]="project().description"
                (ngModelChange)="updateProjectDescription($event)"
                placeholder="What is this project about?"
                required
              />
            </div>
          </div>

          <div class="logo-color-row">
            <div class="form-group">
              <label>Project logo</label>
              <div class="logo-upload-row">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="logoFileInput.click()"
                >
                  <i class="bi bi-image me-2"></i>
                  {{ logoPreviewUrl() ? 'Change logo' : 'Choose logo' }}
                </button>

                <span
                  class="d-inline-block"
                  [ngbTooltip]="
                    !logoPreviewUrl() ? 'No image to remove' : null
                  "
                  container="body"
                  triggers="hover"
                >
                  <button
                    type="button"
                    class="btn btn-outline-danger"
                    [disabled]="isPersisting() || !logoPreviewUrl()"
                    (click)="removeLogo(logoFileInput)"
                  >
                    <i class="bi bi-trash me-2"></i>
                    Remove logo
                  </button>
                </span>

                <!-- @if (logoFile()) {
              <span class="file-note">{{ logoFile()?.name }}</span>
              } @else if (logoPreviewUrl()) {
              <span class="file-note">Existing logo</span>
              } @else {
              <span class="file-note">Optional</span>
              } -->
              </div>
              <input
                #logoFileInput
                type="file"
                id="logoFile"
                name="logoFile"
                accept="image/*"
                class="d-none"
                (change)="onLogoSelected($event)"
              />
            </div>

            <fieldset class="color-fieldset">
              <legend>Project colour</legend>
              <div class="color-swatches">
                @for (option of colorOptions; track option.value) {
                <label
                  class="color-swatch"
                  [class.selected]="option.value === displayColor()"
                  [style.background-color]="option.value"
                >
                  <input
                    class="visually-hidden"
                    type="radio"
                    name="projectColor"
                    [value]="option.value"
                    [checked]="option.value === displayColor()"
                    (change)="updateProjectColor(option.value)"
                  />
                  <span class="visually-hidden">{{ option.label }}</span>
                </label>
                }
              </div>
            </fieldset>
          </div>

          <div class="form-check form-switch">
            <input
              type="checkbox"
              class="form-check-input"
              id="projectBillable"
              name="projectBillable"
              [ngModel]="project().billable"
              (ngModelChange)="updateProjectBillable($event)"
            />
            <label class="form-check-label" for="projectBillable"
              >Billable by default</label
            >
          </div>

          @if (error()) {
          <div class="alert alert-danger mb-0">{{ error() }}</div>
          }
        </form>
      </section>

      <div class="project-side-column">
        <aside class="preview-card">
          <div class="card-header">
            <div>
              <h2 class="eyebrow">Preview</h2>
              <p>
                Keep the essentials tidy so teammates recognize the project
                instantly.
              </p>
            </div>
          </div>

          <div class="project-preview-tile">
            <app-project-preview
              [logoUrl]="logoPreviewUrl()"
              [color]="displayColor()"
              [name]="project().name"
              [description]="project().description"
              namePlaceholder="Your project name"
              descriptionPlaceholder="Add a short description to help teammates understand the project at a glance."
            />
          </div>

          <div class="preview-points">
            <div class="preview-point">
              <i class="bi bi-check2-circle"></i>
              <span>
                Clear project details help teammates find the right workspace
                quickly.</span
              >
            </div>
            <div class="preview-point">
              <i class="bi bi-people"></i>
              <span
                >Project user management becomes available once the project
                already exists.</span
              >
            </div>
            <div class="preview-point">
              <i class="bi bi-shield-check"></i>
              <span
                >Assign the right role so contributors only see the access they
                need.</span
              >
            </div>
          </div>
        </aside>
      </div>
    </div>

    @if (editing() && project().id) {
    <app-project-users
      [project]="project()"
      (projectChange)="onProjectUsersChange($event)"
    />
    } @else {
    <section class="members-card members-card--placeholder">
      <div class="card-header">
        <div>
          <h2 class="eyebrow">Project Users</h2>
          <p>
            Create the project first, then return here to search users and
            assign roles.
          </p>
        </div>
      </div>
    </section>
    }

    <section class="editor-card fields-card">
      <div class="card-header">
        <div>
          <h2 class="eyebrow">Time entry categories</h2>
          <p>
            Suggested categories when logging time on this project. Users can
            also type their own.
          </p>
        </div>
        <div class="fields-header-actions">
          @if (editing() && project().id && areCategoriesDirty()) {
          <span class="field-unsaved-pill">Unsaved changes</span>
          }
          <button
            type="button"
            class="btn btn-outline-secondary"
            (click)="addCategory()"
          >
            <i class="bi bi-plus-lg me-2"></i>
            Add category
          </button>
          <button
            type="button"
            class="btn btn-primary fields-save-button"
            [disabled]="!canSaveCategories() || isPersisting()"
            (click)="saveCategories()"
          >
            @if (isSavingCategories()) {
            <span
              class="spinner-border spinner-border-sm me-2"
              aria-hidden="true"
            ></span>
            } Save categories
          </button>
        </div>
      </div>

      @if (categoriesError()) {
      <div class="alert alert-danger categories-error mb-3">
        {{ categoriesError() }}
      </div>
      } @if (!project().categories.length) {
      <p class="text-muted mb-0">
        No categories configured. Time capture will suggest categories from
        recent entries on the week view. Users can always type their own
        category.
      </p>
      } @else {
      <div class="field-rows">
        @for (category of project().categories; track $index) {
        <article class="field-card">
          <div class="field-card-header">
            <div>
              <p class="field-card-eyebrow">Category {{ $index + 1 }}</p>
            </div>
            <div class="field-row-actions">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                [disabled]="$index === 0"
                (click)="moveCategoryUp($index)"
                aria-label="Move category up"
              >
                <i class="bi bi-arrow-up"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                [disabled]="$index === project().categories.length - 1"
                (click)="moveCategoryDown($index)"
                aria-label="Move category down"
              >
                <i class="bi bi-arrow-down"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                (click)="removeCategory($index)"
                aria-label="Remove category"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
          <div class="field-card-body">
            <div class="form-group mb-0">
              <label [for]="'categoryName' + $index">Name</label>
              <input
                type="text"
                class="form-control"
                [id]="'categoryName' + $index"
                [ngModel]="category"
                (ngModelChange)="updateCategoryAt($index, $event)"
                [name]="'categoryName' + $index"
                placeholder="e.g. Development"
              />
            </div>
          </div>
        </article>
        }
      </div>
      }
    </section>

    <section class="editor-card fields-card">
      <div class="card-header">
        <div>
          <h2 class="eyebrow">Custom fields</h2>
          <p>
            Extra fields that appear on this project's time sheet capture form.
            Leave empty for the standard four-field form.
          </p>
        </div>
        <div class="fields-header-actions">
          @if (editing() && project().id && areFieldsDirty()) {
          <span class="field-unsaved-pill">Unsaved changes</span>
          }
          <button
            type="button"
            class="btn btn-outline-secondary"
            (click)="addField()"
          >
            <i class="bi bi-plus-lg me-2"></i>
            Add field
          </button>
          <button
            type="button"
            class="btn btn-primary fields-save-button"
            [disabled]="!canSaveFields() || isPersisting()"
            (click)="saveFields()"
          >
            @if (isSavingFields()) {
            <span
              class="spinner-border spinner-border-sm me-2"
              aria-hidden="true"
            ></span>
            } Save fields
          </button>
        </div>
      </div>

      @if (fieldsError()) {
      <div class="alert alert-danger fields-error mb-3">
        {{ fieldsError() }}
      </div>
      } @if (!project().fields.length) {
      <p class="text-muted mb-0">No custom fields configured.</p>
      } @else {
      <div class="field-rows">
        @for (field of project().fields; track $index) {
        <article class="field-card">
          <div class="field-card-header">
            <div>
              <p class="field-card-eyebrow">Field {{ $index + 1 }}</p>
              <h3 class="field-card-title">
                {{ field.label.trim() || field.key.trim() || 'New custom field'
                }}
              </h3>
            </div>
            <div class="field-row-actions">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                [disabled]="$index === 0"
                (click)="moveFieldUp($index)"
                aria-label="Move field up"
              >
                <i class="bi bi-arrow-up"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                [disabled]="$index === project().fields.length - 1"
                (click)="moveFieldDown($index)"
                aria-label="Move field down"
              >
                <i class="bi bi-arrow-down"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                (click)="removeField($index)"
                aria-label="Remove field"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>

          <div class="field-card-body">
            <div class="field-row-inputs">
              <div class="form-group">
                <label>Key</label>
                <input
                  type="text"
                  class="form-control"
                  [ngModel]="field.key"
                  (ngModelChange)="updateFieldKey($index, $event)"
                  name="fieldKey{{ $index }}"
                  placeholder="e.g. client-ref"
                />
              </div>
              <div class="form-group">
                <label>Label</label>
                <input
                  type="text"
                  class="form-control"
                  [ngModel]="field.label"
                  (ngModelChange)="updateFieldLabel($index, $event)"
                  name="fieldLabel{{ $index }}"
                  placeholder="Shown to the user"
                />
              </div>
              <div class="form-group">
                <label>Type</label>
                <select
                  class="form-select"
                  [ngModel]="field.type"
                  (ngModelChange)="updateFieldType($index, $event)"
                  name="fieldType{{ $index }}"
                >
                  @for (option of fieldTypeOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                  }
                </select>
              </div>
              @if (field.type === 'select') {
              <div class="form-group">
                <label>Options (comma-separated)</label>
                <input
                  type="text"
                  class="form-control"
                  [ngModel]="fieldOptionsText($index, field)"
                  (ngModelChange)="updateFieldOptionsText($index, $event)"
                  name="fieldOptions{{ $index }}"
                  placeholder="e.g. Draft, Final"
                />
              </div>
              }
              <div class="form-check field-required-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="fieldRequired{{ $index }}"
                  [ngModel]="field.required"
                  (ngModelChange)="updateFieldRequired($index, $event)"
                  name="fieldRequired{{ $index }}"
                />
                <label class="form-check-label" for="fieldRequired{{ $index }}"
                  >Required</label
                >
              </div>
            </div>
          </div>
        </article>
        }
      </div>
      }
    </section>
  </div>
</div>
